import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { FadeUp } from "./Reveal";
import { NoteEyebrow } from "./NoteEyebrow";
import { StackedFrame } from "./StackedFrame";
import { EmSwash } from "./EmSwash";
import { IMAGES } from "../data/content";

const scrollTo = (hash) => (e) => {
  e.preventDefault();
  document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

/**
 * Ways To Give — magazine-style photo tiles.
 *
 * Two large editorial tiles for the two donor doors. Each leads with a
 * StackedFrame photograph and a chapter-style eyebrow, then delivers a
 * short editorial line, a pull-quote, and an ink CTA. This replaces
 * the previous flat 2-card layout that leaned on borders and icons.
 *
 * Photography:
 *   Left  — a real, warm student portrait (Screenshot 2026-07-22 headshot)
 *           so donors see the person they'd be backing.
 *   Right — the community of students (walking photo) to represent the
 *           general fund pooling many gifts into many students.
 */
const PATHS = [
  {
    key: "student",
    title: "Give with a student in mind.",
    body: "Find a verified student whose story speaks to you: books, a flight home, a semester abroad. Then follow their progress as My Rich Aunt\u2019s community of donors fulfills their request.",
    photo: {
      src: IMAGES.studentSteps,
      alt: "A student sitting on stone steps — the kind of face behind every campaign",
    },
    cta: { label: "Browse campaigns", type: "anchor", target: "#campaigns" },
    testid: "give-student",
  },
  {
    key: "fund",
    title: "Give to the fund.",
    body: "My Rich Aunt will distribute your gift among the verified students who need it most. Nothing sits idle.",
    photo: {
      src: IMAGES.classroomHandUp,
      alt: "A student raising her hand in a diverse classroom — the many students one fund can reach",
    },
    cta: { label: "Give to the fund", type: "button" },
    testid: "give-fund",
  },
];

export const WaysToGive = ({ onDonate, onBrowse }) => (
  <section id="give" className="bg-secondary">
    <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-32">
      <FadeUp>
        <NoteEyebrow accent="bg-cobalt">ways to give</NoteEyebrow>
        <h2 className="mt-5 max-w-2xl font-serif text-3xl lg:text-[3.5rem] leading-[1.02] tracking-tight">
          Two ways to <EmSwash color="text-cobalt">change a life.</EmSwash>
        </h2>
      </FadeUp>

      <div className="mt-16 grid grid-cols-1 gap-14 md:grid-cols-2 md:gap-8">
        {PATHS.map((p, i) => (
          <FadeUp key={p.key} delay={i * 0.1} className="h-full">
            <article
              className="flex h-full flex-col"
              data-testid={`${p.testid}-card`}
            >
              <div className="w-full max-w-[480px]">
                <StackedFrame offset="md">
                  <div className="relative aspect-[4/3] overflow-hidden rounded-md">
                    <img
                      src={p.photo.src}
                      alt={p.photo.alt}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </StackedFrame>
              </div>

              <div className="mt-8 flex flex-1 flex-col">
                <h3 className="font-serif text-3xl leading-tight text-ink">
                  {p.title}
                </h3>
                <p className="mt-5 mb-8 max-w-md text-base leading-relaxed text-olive">
                  {p.body}
                </p>

                {p.cta.type === "anchor" ? (
                  onBrowse ? (
                    <button
                      type="button"
                      onClick={onBrowse}
                      data-testid={`${p.testid}-button`}
                      className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
                    >
                      {p.cta.label} <ArrowRight className="h-4 w-4" strokeWidth={2} />
                    </button>
                  ) : (
                    <a
                      href={p.cta.target}
                      onClick={scrollTo(p.cta.target)}
                      data-testid={`${p.testid}-button`}
                      className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
                    >
                      {p.cta.label} <ArrowRight className="h-4 w-4" strokeWidth={2} />
                    </a>
                  )
                ) : (
                  <button
                    type="button"
                    onClick={onDonate}
                    data-testid={`${p.testid}-button`}
                    className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
                  >
                    {p.cta.label} <ArrowRight className="h-4 w-4" strokeWidth={2} />
                  </button>
                )}
              </div>
            </article>
          </FadeUp>
        ))}
      </div>

      <FadeUp>
        <p className="mt-10 max-w-3xl text-base leading-relaxed text-olive" data-testid="ways-to-give-footnote">
          When you back a particular student&rsquo;s campaign, you help My Rich Aunt see which stories resonate most with donors. Every donation is made to My Rich Aunt, a registered 501(c)(3) organization, which awards grants to verified students for{" "}
          <Link to="/faq?group=students&open=expense-guidelines" className="font-semibold text-ink underline-offset-2 hover:underline" data-testid="ways-to-give-expenses-link">
            eligible expenses
          </Link>.{" "}
          <Link to="/faq?group=donors&open=who-receives" className="font-semibold text-ink underline-offset-2 hover:underline" data-testid="ways-to-give-faq-link">
            More in the FAQs
          </Link>
        </p>
      </FadeUp>
    </div>
  </section>
);
